import type { SubscriptionRenewal } from '@/lib/api/services/subscriptions';

/**
 * Wording and colours for the "Renew Now" prompts. WHETHER to show one, the
 * days left and the severity all come from the server's `renewal` object
 * (GET /subscriptions/current); this file only turns that into words, so the
 * top banner and the Billing page always say the same thing.
 */

const PERIOD_LABEL: Record<string, string> = {
  monthly: '1 month',
  quarterly: '3 months',
  half_yearly: '6 months',
  yearly: '1 year',
  annual: '1 year',
};

export type RenewalSeverity = NonNullable<SubscriptionRenewal['severity']>;

/** Banner background by severity (notice → warning → critical). */
export const RENEWAL_BANNER_CLASSES: Record<RenewalSeverity, string> = {
  notice: 'bg-amber-100 text-amber-950 dark:bg-amber-900/60 dark:text-amber-50',
  warning: 'bg-orange-500 text-white dark:bg-orange-600',
  critical: 'bg-red-600 text-white dark:bg-red-700',
};

/** Billing-page notice card by severity. */
export const RENEWAL_NOTICE_CLASSES: Record<RenewalSeverity, string> = {
  notice: 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-50',
  warning: 'border-orange-300 bg-orange-50 text-orange-950 dark:border-orange-700 dark:bg-orange-950/40 dark:text-orange-50',
  critical: 'border-red-300 bg-red-50 text-red-950 dark:border-red-800 dark:bg-red-950/40 dark:text-red-50',
};

/** A renewal the client can act on now (inside the window, or expired). */
export function actionableRenewal(renewal: SubscriptionRenewal | null | undefined): SubscriptionRenewal | null {
  return renewal?.eligible && renewal.severity ? renewal : null;
}

/** "today" / "tomorrow" / "in 5 days". */
export function renewalDaysLabel(daysLeft: number | null): string {
  if (daysLeft == null) return '';
  if (daysLeft <= 0) return 'today';
  if (daysLeft === 1) return 'tomorrow';
  return `in ${daysLeft} days`;
}

/** Period end as "22 Oct 2026" — the last day the plan works. */
export function renewalEndDate(renewal: SubscriptionRenewal): string {
  if (!renewal.period_end) return '';
  return new Date(renewal.period_end).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** "₹8,399 for 1 month". */
export function renewalPriceLabel(renewal: SubscriptionRenewal): string {
  if (renewal.amount == null) return '';
  const amount = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: renewal.currency || 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(renewal.amount) ? 0 : 2,
  }).format(renewal.amount);
  const period = PERIOD_LABEL[String(renewal.billing_period || '').toLowerCase()];
  return period ? `${amount} for ${period}` : amount;
}

/** One-line banner message. */
export function renewalBannerText(renewal: SubscriptionRenewal): string {
  const plan = renewal.plan_name ? `Your ${renewal.plan_name} plan` : 'Your plan';
  if (renewal.stage === 'expired') return `${plan} has expired. Renew now to continue.`;
  return `${plan} expires ${renewalDaysLabel(renewal.days_left)} (${renewalEndDate(renewal)}). Renew now to avoid interruption.`;
}

/** Billing-page notice: upcoming invoice vs payment due vs expired. */
export function renewalNoticeCopy(renewal: SubscriptionRenewal): { title: string; description: string } {
  if (renewal.stage === 'expired') {
    return {
      title: 'Your subscription has expired',
      description: 'Renew now to get your account working again.',
    };
  }
  if (renewal.stage === 'due') {
    return {
      title: 'Your subscription payment is due',
      description: `Renew now to keep your subscription active. Your plan ends ${renewalDaysLabel(renewal.days_left)} (${renewalEndDate(renewal)}).`,
    };
  }
  return {
    title: 'Your subscription invoice is due soon',
    description: `Renew your plan to continue uninterrupted service. Your plan ends on ${renewalEndDate(renewal)}.`,
  };
}
